import { List, RecordOf } from '@opengov/compose-app/immutable';
import { BasicColumnTypes } from '@opengov/ogql-types'; // the possible units by which a dataset *might* be organized

export var CalendarUnit;

(function (CalendarUnit) {
  CalendarUnit["Day"] = "day";
  CalendarUnit["FiscalPeriod"] = "fiscalPeriod";
  CalendarUnit["FiscalQuarter"] = "fiscalQuarter";
  CalendarUnit["FiscalYear"] = "fiscalYear";
  CalendarUnit["Month"] = "month";
  CalendarUnit["Quarter"] = "quarter";
  CalendarUnit["Week"] = "week";
  CalendarUnit["Year"] = "year";
})(CalendarUnit || (CalendarUnit = {}));

export var EReportColumnTypes;

(function (EReportColumnTypes) {
  EReportColumnTypes["storage"] = "storage";
  EReportColumnTypes["infix"] = "infix";
  EReportColumnTypes["conditional"] = "conditional";
  EReportColumnTypes["percentOfTotal"] = "percentOfTotal";
  EReportColumnTypes["constant"] = "constant";
  EReportColumnTypes["aggregate"] = "aggregate";
  EReportColumnTypes["targetStatus"] = "targetStatus";
})(EReportColumnTypes || (EReportColumnTypes = {}));

export var EOgqlInfixColumnOperators;

(function (EOgqlInfixColumnOperators) {
  EOgqlInfixColumnOperators["add"] = "+";
  EOgqlInfixColumnOperators["divide"] = "/";
  EOgqlInfixColumnOperators["multiply"] = "*";
  EOgqlInfixColumnOperators["subtract"] = "-";
})(EOgqlInfixColumnOperators || (EOgqlInfixColumnOperators = {}));

export var EOgqlAggregateColumnOperators;

(function (EOgqlAggregateColumnOperators) {
  EOgqlAggregateColumnOperators["count"] = "count";
  EOgqlAggregateColumnOperators["sum"] = "sum";
})(EOgqlAggregateColumnOperators || (EOgqlAggregateColumnOperators = {}));

export var EConditionalComparisonOperators;

(function (EConditionalComparisonOperators) {
  EConditionalComparisonOperators["greaterThan"] = "greaterThan";
  EConditionalComparisonOperators["greaterThanOrEqualTo"] = "greaterThanOrEqualTo";
  EConditionalComparisonOperators["lessThan"] = "lessThan";
  EConditionalComparisonOperators["lessThanOrEqualTo"] = "lessThanOrEqualTo";
  EConditionalComparisonOperators["equalTo"] = "equalTo";
  EConditionalComparisonOperators["notEqualTo"] = "notEqualTo";
  EConditionalComparisonOperators["between"] = "between"; // inclusive between e.g. `10 <= x <= 20`
})(EConditionalComparisonOperators || (EConditionalComparisonOperators = {}));

export var EReportConstant;

(function (EReportConstant) {
  EReportConstant["Day"] = "day";
  EReportConstant["Month"] = "month";
  EReportConstant["Week"] = "week";
  EReportConstant["Quarter"] = "quarter";
  EReportConstant["Year"] = "year";
  EReportConstant["Number"] = "number";
  EReportConstant["Percentage"] = "percentage";
  EReportConstant["Date"] = "date";
  EReportConstant["Text"] = "text";
})(EReportConstant || (EReportConstant = {}));

export var newCondition = RecordOf({
  operator: null,
  matchValue: null,
  returnedValue: null
});
export var NewCondition = function NewCondition(rs) {
  return !rs ? newCondition() : newCondition(rs).set('matchValue', mapColumnToRecord(rs.matchValue));
};
export var newConditionBetween = RecordOf({
  operator: null,
  lowerBound: null,
  upperBound: null,
  returnedValue: null
});
export var NewConditionBetween = function NewConditionBetween(rs) {
  return !rs ? newConditionBetween() : newConditionBetween(rs).set('lowerBound', mapColumnToRecord(rs.lowerBound)).set('upperBound', mapColumnToRecord(rs.upperBound));
};
export var ReportColumnMetaDataTagsRecord = RecordOf({
  hidden: null,
  units: null
});
export var ReportColumnMetaDataFactsRecord = RecordOf({
  distinctCount: null,
  minimum: null,
  maximum: null
});
export var reportColumnMetaDataRecord = RecordOf({
  tags: ReportColumnMetaDataTagsRecord(),
  facts: ReportColumnMetaDataFactsRecord(),
  units: null
});
export var ReportColumnMetaDataRecord = function ReportColumnMetaDataRecord(rs) {
  return !rs ? reportColumnMetaDataRecord() : reportColumnMetaDataRecord(rs).set('tags', rs.tags ? ReportColumnMetaDataTagsRecord(rs.tags) : ReportColumnMetaDataTagsRecord()).set('facts', rs.facts ? ReportColumnMetaDataFactsRecord(rs.facts) : ReportColumnMetaDataFactsRecord());
};
export var storageReportColumnRecord = RecordOf({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.storage,
  hidden: false,
  semanticTypes: List(),
  metadata: ReportColumnMetaDataRecord()
});
var makeSemanticTypeRecord = RecordOf({
  attributes: null,
  name: null,
  type: null,
  units: ''
});

function nullOutNonValueType(record) {
  return record.set('attributes', null);
} // The current mapping of SemanticType does not correctly account for the attributes nested type. When these
// types are constructed, it will get retained as a JS Object, which then breaks immutable value comparison for
// anything these are in.
//
// As we don't use semantic types yet, we can null them out to retain the value comparison.


function IOgqlSemanticTypeRecord(semanticType) {
  return nullOutNonValueType(makeSemanticTypeRecord(semanticType));
}

export var StorageReportColumnRecord = function StorageReportColumnRecord(rs) {
  return !rs ? storageReportColumnRecord() : storageReportColumnRecord(rs).set('semanticTypes', rs.semanticTypes ? List(rs.semanticTypes.map(IOgqlSemanticTypeRecord)) : List()).set('metadata', rs.metadata ? ReportColumnMetaDataRecord(rs.metadata) : ReportColumnMetaDataRecord());
};
export var InfixReportColumnRecord = RecordOf({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.infix,
  hidden: false,
  leftColumn: StorageReportColumnRecord(),
  rightColumn: StorageReportColumnRecord(),
  operator: null
});
export var PercentOfTotalReportColumnRecord = function PercentOfTotalReportColumnRecord(rs) {
  return !rs ? percentOfTotalReportColumnRecord() : percentOfTotalReportColumnRecord(rs).set('excludedCategorizedByValues', rs.excludedCategorizedByValues ? List(rs.excludedCategorizedByValues) : List());
};
export var percentOfTotalReportColumnRecord = RecordOf({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.percentOfTotal,
  hidden: false,
  excludedCategorizedByValues: List(),
  originalColumnType: EReportColumnTypes.storage,
  sourceColumn: null,
  conditions: null,
  else: null,
  leftColumn: null,
  rightColumn: null,
  operator: null
});
export var ConstantReportColumnRecord = RecordOf({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.constant,
  hidden: false,
  value: null,
  unit: EReportConstant.Number
});
export var ConditionalReportColumnRecord = function ConditionalReportColumnRecord(rs) {
  return !rs ? conditionalReportColumnRecord() : conditionalReportColumnRecord(rs).set('metadata', rs.metadata ? ReportColumnMetaDataRecord(rs.metadata) : ReportColumnMetaDataRecord()).set('sourceColumn', rs.sourceColumn ? mapColumnToRecord(rs.sourceColumn) : StorageReportColumnRecord()).set('conditions', rs.conditions ? List(getListOfConditions(rs.conditions)) : List());
};

var getListOfConditions = function getListOfConditions(conditions) {
  var conditionList = [];
  conditions.forEach(function (condition) {
    condition.operator === EConditionalComparisonOperators.between ? conditionList.push(NewConditionBetween(condition)) : conditionList.push(NewCondition(condition));
  });
  return conditionList;
};

export var conditionalReportColumnRecord = RecordOf({
  name: '',
  displayName: '',
  type: BasicColumnTypes.Text,
  columnType: EReportColumnTypes.conditional,
  hidden: false,
  sourceColumn: StorageReportColumnRecord(),
  conditions: List(),
  else: '',
  metadata: ReportColumnMetaDataRecord()
});
export var AggregateReportColumnRecord = RecordOf({
  name: EOgqlAggregateColumnOperators.count,
  aggregateType: EOgqlAggregateColumnOperators.count,
  displayName: 'Count',
  type: BasicColumnTypes.Numeric,
  columnType: EReportColumnTypes.aggregate,
  hidden: false
});
export var TargetStatusReportColumnRecord = RecordOf({
  name: EReportColumnTypes.targetStatus,
  displayName: 'Target Status',
  type: BasicColumnTypes.Numeric,
  columnType: EReportColumnTypes.targetStatus,
  hidden: false
});
export var mapColumnToRecord = function mapColumnToRecord(column) {
  switch (column.columnType) {
    case EReportColumnTypes.infix:
      return InfixReportColumnRecord(column).set('leftColumn', mapColumnToRecord(column.leftColumn)).set('rightColumn', mapColumnToRecord(column.rightColumn));

    case EReportColumnTypes.conditional:
      return ConditionalReportColumnRecord(column);

    case EReportColumnTypes.constant:
      return ConstantReportColumnRecord(column);

    case EReportColumnTypes.percentOfTotal:
      return PercentOfTotalReportColumnRecord(column);

    case EReportColumnTypes.aggregate:
      return AggregateReportColumnRecord(column);

    case EReportColumnTypes.targetStatus:
      return TargetStatusReportColumnRecord(column);

    case EReportColumnTypes.storage:
    default:
      return StorageReportColumnRecord(column);
  }
};
export var getDisplayName = function getDisplayName(column, columnNames) {
  // apparently sometimes columnNames doesn't have a find method, even if it's an Immutable List
  if (columnNames.find && columnNames.find(function (r) {
    return r.originalName === column.name;
  })) {
    return columnNames.find(function (r) {
      return r.originalName === column.name;
    }).displayName;
  }

  return column.displayName;
};
export var AGGREGATE = 'Aggregate';